// @avantia common-code
import { ensureDefined, isTrue, MicroTimer, SMap } from '@avantia/client-and-server-utilities';
import { HpField, HpFields, HpFieldValue, validateFields } from '@avantia/form-validation';
import { lodash } from '@avantia/lodash';
import { hasOwnProperty, HpError } from '@avantia/logging-and-errors';
import {
  createControlId,
  destructureControlId,
  getIdentifierConfigDefaultValues,
  HpAddControlGroupActionProps,
  HpAnswer,
  HpAnswerMap,
  HpAnswerValueTypes,
  HpCreateControlIdOptions,
  HpIdentifierAndFunctionValues,
  HpQuestion,
  HpQuestionDataTypes,
  HpQuestionOrderTypes,
  identifierConfigChanged,
  loadQuestionIdCache
} from '@avantia/questionset-model';
import { v4 as uuid } from 'uuid';
import { HpClientDebugFlags } from '../clientDebugFlags';
import {
  displayTimeTaken,
  logError,
  logInfo,
  logWarning,
  recordElapsedTime,
  recordElapsedTimeOfFunc
} from '../clientLogging';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { getCustomExtension } from '../customExtensionRegistry';
import {
  HpValidateAndMaybeMoveSectionExtensionFunction,
  HpValidateAndMaybeMoveSectionProps
} from '../customExtensionRegistryTypes';
import {
  getMetaDataOrUndefinedFromAnswers,
  HpMetaData,
  setMetaDataInAnswers,
  sortAnswerMap
} from '../tools/clientAndServerUtilities';
import { setProductIdCookie } from '../tools/cookieUtilities';
import {
  getSuggestionsFromMapUsingSearchTerm,
  HpGetSuggestionsFromMapUsingSearchTermProps
} from '../tools/listSearchUtility';
import { HpClientPrerequisiteThisReferenceClass } from '../tools/prerequisiteThisReference';
import { setActiveSection } from '../tools/questionDisplayStateTools';
import { getQuestionIdFor, getSectionIdFor } from '../tools/questionIds';
import {
  applyAnswerDefaults,
  evaluateCalculatedAnswer,
  HpConditionMetFunction,
  runDependentActions
} from '../tools/questionPrerequisites';
import {
  answersAreSame,
  convertSkewerToCamelCase,
  getAnswerValue,
  HpAnswerValueQuestionType
} from '../tools/utilities';
import {
  HpClientAnswer,
  HpClientAnswerMap,
  HpClientConditionFunction,
  HpClientConditionFunctionProps,
  HpClientDependentAction,
  HpClientDependentAnswer,
  HpClientPartialAnswer,
  HpClientPrerequisiteThisReference,
  HpClientQuestion,
  HpClientQuestionData,
  HpClientQuestionLookupItem,
  HpClientQuestionMap,
  HpClientQuestionSetState,
  HpClientQuestionUI,
  HpClientQuestionUiMap,
  HpClientSectionMap,
  HpClientValidationResult,
  HpClientValidationThisReference,
  HpDateDropDownEventDetail,
  HpEventDetail,
  HpEventTypes,
  HpFieldChangedPayload,
  HpQuestionDisplayState,
  HpQuestionScriptAction,
  HpScriptAction,
  HpScriptActionTypes,
  HpWhenToEvaluatePrerequisitesTypes,
  OptionalQuestionUI
} from '../types';
import { changeQuestionSet, createAnswer, HpIsValidControlTypeFunction } from './questionSetPayloadMapper';
import {
  canMoveToDesiredSection,
  createBlankAnswer,
  createQuestionIdSorter,
  determineHighestSectionStartedPosition,
  determineVisibleQuestions,
  evaluateCalculatedAnswers,
  evaluateLookupExpressions,
  evaluateQuestionPrerequisites,
  evaluateSectionPrerequisites,
  getActiveSection,
  getTypeOfQuestion,
  HpMoveToSectionTypes,
  iterateVisibleQuestions,
  validateSection
} from './questionSetTools';
import { HpVisibleQuestionCustomization } from './questionSetToolsCustomizationInterface';
import { visibleQuestionDefaultCustomization } from './questionSetToolsDefaultCustomization';
import { visibleQuestionDescriptiveCustomization } from './questionSetToolsDescriptiveCustomization';
import { applyChanges, applyInnerChanges } from './reducerLibrary';

const moveToSectionType: HpMoveToSectionTypes = 'move-to-section';
let whenToEvaluatePrerequisites: HpWhenToEvaluatePrerequisitesTypes | undefined;
let validateOnBackNavigation = true;
let isDeveloperEnv = false;
const defaultScrollPreference: HpScriptActionTypes = 'scroll-to-top';
let scrollPreference: HpScriptActionTypes;
let {
  groupStartNumber,
  maxGroupsAllowed,
  groupIndexWidth,
  maxGroupNesting,
  multiplePerIndex,
  questionOrderPropertyIsString,
  useDescriptiveQuestionIds
} = addCalculationsToIdentierConfig(getIdentifierConfigDefaultValues());

registerConfigurationAction((config) => {
  const { environment: env } = config;
  isDeveloperEnv = env === 'developer' || env === 'unit-tests';
  ({
    whenToEvaluatePrerequisites,
    validateOnBackNavigation = true,
    scrollPreference = defaultScrollPreference
  } = config);
});

identifierConfigChanged((config) => {
  ({
    groupStartNumber,
    maxGroupsAllowed,
    groupIndexWidth,
    maxGroupNesting,
    multiplePerIndex,
    questionOrderPropertyIsString,
    useDescriptiveQuestionIds
  } = addCalculationsToIdentierConfig(config));
});

export function getVisibleQuestionCustomization(productId: string): HpVisibleQuestionCustomization {
  const useDescriptive = useDescriptiveQuestionIds[productId] || useDescriptiveQuestionIds['*'];
  return useDescriptive ? visibleQuestionDescriptiveCustomization : visibleQuestionDefaultCustomization;
}

export type HpStateChangeClosure = (state: HpClientQuestionSetState) => HpClientQuestionSetState;

export function changeState(state: HpClientQuestionSetState, change: HpStateChangeClosure): HpClientQuestionSetState {
  return change({ ...state });
}

export type HpGetFieldDataResponse = HpClientQuestionUI;

export function createValidationModel(state: HpClientQuestionSetState, sectionId?: string): HpFields {
  const { answers, commonLookups, displayState, productId, questions } = state;
  const { sections } = displayState;
  loadQuestionIdCache({ items: questions, onlyIfEmpty: true });
  const self = new HpClientPrerequisiteThisReferenceClass({
    answers,
    commonLookups,
    debugFlag: HpClientDebugFlags.ValidatorExpressions,
    displayState,
    groupNumber: undefined,
    indexes: undefined,
    productId,
    questions,
    sections,
    sourceQuestionId: '',
    sourceQuestionType: undefined
  });

  return {
    get(key: string): HpField {
      const question: HpClientQuestion | undefined = key ? questions[key] : undefined;
      if (!question) {
        return resolveFieldLikeValue(key, questions);
      }

      const data = question.data as HpClientQuestionData;
      const answer: HpAnswer = answers[key]; // An answer should be undefined for buttons.
      let value: HpFieldValue = null;

      // eslint-disable-next-line prefer-const
      let { answerValue, answerSource } = getAnswerValue(answer, question, 'validation');
      const answerType = typeof answerValue;
      if (answerSource === 'unanswered' && question.default !== undefined && question.isDefaultImplicit) {
        answerValue = question.default;
      }

      if (answerType === 'string') {
        value = answerValue as string;
      } else {
        if (answerValue === undefined || answerValue === null) {
          value = null;
        } else if (lodash.isArray(answerValue)) {
          value = answerValue.map(String);
        } else if (lodash.isObject(answerValue)) {
          // The answerValue shouldn't usually be an object, but in special cases, it is, so handle it accordingly.
          value = JSON.stringify(answerValue);
        } else {
          value = String(answerValue);
        }
      }

      return {
        key: question.name,
        name: question.name,
        label: question.label || question.name,
        value,
        validators: data.validation
      };
    },
    set(): void {
      throw new Error('Intentionally not implemented');
    },
    exists(key: string): boolean {
      return !!questions[key];
    },
    getAllKeys(): string[] {
      const keys: string[] = [];
      const sId = sectionId || getActiveSection(displayState).sectionId;
      iterateVisibleQuestions({
        questions,
        sections,
        questionsUI: displayState.questionsUI,
        section: sections[sId],
        action: ({ questionId }) => {
          keys.push(questionId);
        }
      });
      return keys;
    },
    getData(key: string): HpGetFieldDataResponse {
      return displayState.questionsUI[key];
    },
    getThis(): HpClientValidationThisReference<HpClientQuestion> {
      return self;
    }
  };
}

export interface HpFieldChangedHandlerProps {
  isDelayedValidation: boolean;
  payload: HpFieldChangedPayload | undefined;
  state: HpClientQuestionSetState;
}

export const isValidControlTypeVoid: HpIsValidControlTypeFunction = () => true;

let isValidControlTypeFunc: HpIsValidControlTypeFunction = isValidControlTypeVoid;

export function registerIsValidControlTypeFunction(func: HpIsValidControlTypeFunction): void {
  isValidControlTypeFunc = ensureDefined(func, 'IsValidControlTypeFunction');
}

export function getIsValidControlTypeFunction(): HpIsValidControlTypeFunction {
  return isValidControlTypeFunc;
}

export function fieldChangedHandler({
  isDelayedValidation,
  payload,
  state: currState
}: HpFieldChangedHandlerProps): HpClientQuestionSetState {
  if (!payload) {
    return currState;
  }

  const timer = new MicroTimer().start();
  const { getEvent, key: sourceId, ui, value } = payload;
  const event = getEvent();
  const clearPartialAnswer = (event as any).hpClearPartialAnswer;
  const eventType = event.type as HpEventTypes;
  const { questionId: qid, groupNumber, indexes } = destructureControlId(sourceId, { requirePrefix: false });
  const questionId = createControlId({ questionId: qid, groupNumber, indexes, options: { noPrefix: true } });

  if (questionId === 'questionSetId') {
    let newState = changeState(currState, (state) => changeQuestionSet(state, value, isValidControlTypeFunc));
    newState = processPrereqsAndCalcs(eventType, newState);
    const { answers: oldAnswers, productId, questionSetId, version } = newState;
    const answers = { ...oldAnswers };
    const metadata: HpMetaData = { productId, questionSetId, version };
    setMetaDataInAnswers({ productId, answers, metadata });
    newState.answers = answers;
    setProductIdCookie(productId);
    return newState;
  }

  // If we don't return here, when a customer focuses upon a field, after the delay, they'll get an error
  //  message, when in fact they may just be thinking about the answer.
  if (isDelayedValidation && value === '') {
    return currState;
  }

  let isChanged: HpIsStateChanged | undefined;
  if (!isDelayedValidation) {
    ({ state: currState, isChanged } = updateAnswer({
      currState,
      questionId,
      value,
      eventType,
      clearPartialAnswer
    }));
  }

  const validationResult = validateAnswer({
    questionId,
    eventType,
    isDelayedValidation,
    answers: currState.answers,
    currState
  });

  let newState: HpClientQuestionSetState;
  if (!isDelayedValidation) {
    let { questionsUI } = currState.displayState;
    const { touchedUi, questionIdWithFocus } = getTouchedUi(eventType, ui, questionId);
    if (touchedUi) {
      questionsUI = { ...questionsUI };
      questionsUI[questionId] = applyChanges(questionsUI[questionId], touchedUi);
    }

    newState = applyChanges(currState, { validationResult });
    newState = processPrereqsAndCalcs(eventType, newState, { questionsUI });

    if (isChanged && isChanged.answers) {
      const { productId } = newState;
      const metadata: Partial<HpMetaData> = getMetaDataOrUndefinedFromAnswers(productId, newState.answers) || {};
      const { highestSectionStartedPosition } = determineHighestSectionStartedPosition(
        newState.displayState,
        metadata.isJourneyComplete === true
      );
      newState.displayState = applyChanges(newState.displayState, {
        highestSectionStartedPosition,
        incrementedSectionId: undefined,
        questionIdWithFocus
      });
    }
  } else {
    newState = applyChanges(currState, { validationResult });
  }

  displayTimeTaken('fieldChangedHandler', timer);

  return newState;
}

interface HpGetTouchedUiResponse {
  questionIdWithFocus: string | undefined;
  touchedUi: OptionalQuestionUI;
}

function getTouchedUi(
  eventType: HpEventTypes,
  ui: Partial<HpClientQuestionUI> | undefined,
  questionId: string
): HpGetTouchedUiResponse {
  let touchedUi: OptionalQuestionUI;
  let questionIdWithFocus: string | undefined;
  const isBlur = eventType === 'blur';
  if (isBlur || eventType === 'click') {
    touchedUi = { isTouched: true };
    if (isBlur) {
      touchedUi.hasFocus = undefined;
    }
  } else if (eventType === 'change') {
    touchedUi = { isTouched: true, isDirty: true, hasFocus: true };
    questionIdWithFocus = questionId;
  }

  if (ui && touchedUi) {
    const tmp = applyChanges(touchedUi, ui);
    if (!touchedUi.hasFocus) {
      tmp.hasFocus = undefined;
    }

    touchedUi = tmp;
  } else if (ui) {
    touchedUi = ui;
  }

  return { touchedUi, questionIdWithFocus };
}

function resolveFieldLikeValue(key: string, questions: HpClientQuestionMap): HpField {
  if (key && key.indexOf(':')) {
    const parts = key.split(':');
    if (parts.length === 2) {
      switch (parts[0]) {
        case 'question': {
          const question = questions[parts[1]];
          if (question) {
            // We need to return the question so have to pretend it is an HpField.
            // This allows custom validators to get hold of question data when required.
            return question as unknown as HpField;
          }
        }
      }
    }

    throw new Error(
      `There is no way to resolve key "${key}". Use either '<questionId>' or 'question:<questionId>', where <questionId> is usually a UUID.`
    );
  }

  throw new Error(`There is no field with key "${key}".`);
}

interface ShouldPrereqsRunProps {
  currentCall: 'first' | 'last';
  whenToEvaluatePrerequisites: HpWhenToEvaluatePrerequisitesTypes | undefined;
}

export function shouldPrereqsRun({ currentCall, whenToEvaluatePrerequisites }: ShouldPrereqsRunProps): boolean {
  if (currentCall === 'first') {
    return (
      !whenToEvaluatePrerequisites || whenToEvaluatePrerequisites === 'first' || whenToEvaluatePrerequisites === 'both'
    );
  }

  return whenToEvaluatePrerequisites === 'last' || whenToEvaluatePrerequisites === 'both';
}

export function processPrereqsAndCalcs(
  eventType: HpEventTypes,
  state: HpClientQuestionSetState,
  displayStateChanges?: Partial<HpQuestionDisplayState>
): HpClientQuestionSetState {
  return recordElapsedTime('processPrereqsAndCalcs', () => {
    if (displayStateChanges) {
      state = applyChanges(state, { displayState: applyChanges(state.displayState, displayStateChanges) });
    }

    if (shouldPrereqsRun({ currentCall: 'first', whenToEvaluatePrerequisites })) {
      state = evaluateQuestionPrerequisites(state, eventType);
    }

    state = evaluateLookupExpressions(state);
    state = evaluateCalculatedAnswers(state, eventType);
    if (shouldPrereqsRun({ currentCall: 'last', whenToEvaluatePrerequisites })) {
      state = evaluateQuestionPrerequisites(state, eventType);
    }

    const { productId } = state;
    let { displayState } = state;
    const sectionsUI = evaluateSectionPrerequisites(displayState, state, eventType);
    displayState = applyChanges(displayState, {
      visibleQuestionIds: determineVisibleQuestions({
        questions: state.questions,
        questionsUI: displayState.questionsUI,
        activeSection: displayState.sections[displayState.activeSectionId],
        sections: displayState.sections,
        customization: getVisibleQuestionCustomization(productId)
      }),
      sectionsUI
    });
    state.displayState = displayState;
    return state;
  });
}

interface ValidateAnswerProps {
  answers: HpClientAnswerMap;
  currState: HpClientQuestionSetState;
  eventType: HpEventTypes;
  isDelayedValidation: boolean;
  questionId: string;
}

function validateAnswer({
  currState,
  eventType,
  isDelayedValidation,
  questionId
}: ValidateAnswerProps): HpClientValidationResult {
  const { validationResult } = currState;
  const errors = validationResult.errors;
  const prevError = errors[questionId];
  const isInstantType = eventType === 'blur' || eventType === 'click';
  let validation: HpClientValidationResult | undefined;
  if (isInstantType || prevError || isDelayedValidation) {
    validation = lodash.cloneDeep(validationResult);
    validation = {
      ...validation,
      ...validateFields({
        fields: createValidationModel(currState),
        fieldKeysToValidate: [questionId],
        previousResult: lodash.cloneDeep(validation),
        eventType,
        allowPartialValidation: true
      })
    };
    if (prevError && !(isDelayedValidation || isInstantType)) {
      // Don't change the error
      if (validation.errors[questionId]) {
        validation.errors[questionId] = prevError;
      }
    }
  }

  return validation || validationResult;
}

export interface HpUpdateAnswersProps {
  questionIds: string[];
  state: HpClientQuestionSetState;
}

export function updateDefaultedAnswerDependencies({
  questionIds,
  state
}: HpUpdateAnswersProps): HpClientQuestionSetState {
  for (let i = 0; i < questionIds.length; i++) {
    const questionId = questionIds[i];
    const value = state.answers[questionId].default;
    ({ state } = updateAnswer({
      currState: state,
      questionId,
      value,
      eventType: 'default-answer'
    }));
  }

  return state;
}

export interface HpUpdateAnswerProps {
  clearPartialAnswer?: boolean;
  currState: HpClientQuestionSetState;
  eventType: HpEventTypes;
  partialAnswer?: HpClientPartialAnswer; // This will override the 'value' parameter.
  questionId: string;
  value: string | HpAnswerValueTypes;
}

interface UpdateAnswerResult {
  isChanged: HpIsStateChanged;
  state: HpClientQuestionSetState;
}

export function updateAnswer({
  clearPartialAnswer,
  currState,
  eventType,
  partialAnswer,
  questionId,
  value
}: HpUpdateAnswerProps): UpdateAnswerResult {
  const updateDependenciesOnly = eventType === 'default-answer';
  const { questions } = currState;
  const isChanged: HpIsStateChanged = {
    answers: false,
    displayState: false,
    displayStateQuestionsUI: false
  };

  // eslint-disable-next-line prefer-const
  let { answers, productId } = currState;
  const question = questions[questionId];
  const { data, dataType } = question;

  if (dataType === 'button') {
    // Button actions should be performed in an action
    // and should not cause cascading as questions do.
    return { state: currState, isChanged };
  }

  if (!updateDependenciesOnly) {
    const updatedAnswers = updateAnswerValue({
      question,
      answers,
      partialAnswer,
      value,
      dataType,
      eventType,
      clearPartialAnswer
    });
    if (updatedAnswers) {
      answers = updatedAnswers;
      isChanged.answers = true;
      currState = applyChanges(currState, { answers });
    }
  }

  const { dependentAnswers, dependentActions } = data as HpClientQuestionData;

  if (dependentAnswers) {
    currState = updateAnswerDependentAnswers({
      sourceQuestionId: questionId,
      question,
      state: currState,
      dependentAnswers,
      answerType: updateDependenciesOnly ? 'default' : 'customer',
      eventType,
      isChanged
    });
  }

  if (dependentActions && dependentActions.length > 0) {
    currState = updateAnswerDependentActions({
      questionId,
      question,
      eventType,
      dependentActions,
      state: currState
    });
  }

  // The 'showParticularSection' question used to be dataType:boolean, so we check for
  // the value being a string to prevent incorrect behaviour (mostly in unit tests).
  if (questionId === getQuestionIdFor(productId, 'showParticularSection') && typeof value === 'string' && !!value) {
    const activeSectionId = getSectionIdFor(productId, convertSkewerToCamelCase(value as string));
    currState.displayState = applyChanges(currState.displayState, { activeSectionId });
    currState.answers = applyChanges(currState.answers, {
      [getQuestionIdFor(productId, 'activeSectionId')]: { customer: activeSectionId, default: null }
    });
  }

  return { state: currState, isChanged };
}

export interface HpIsStateChanged {
  answers: boolean;
  displayState: boolean;
  displayStateQuestionsUI: boolean;
}

export interface HpUpdateAnswerDependentAnswersProps {
  answerType: keyof HpAnswer;
  dependentAnswers: SMap<HpClientDependentAnswer[]>;
  eventType: HpEventTypes;
  isChanged: HpIsStateChanged;
  question: HpClientQuestion;
  sourceQuestionId: string;
  state: HpClientQuestionSetState;
}

export function updateAnswerDependentAnswers({
  answerType,
  dependentAnswers,
  eventType,
  isChanged,
  question,
  sourceQuestionId,
  state
}: HpUpdateAnswerDependentAnswersProps): HpClientQuestionSetState {
  if (lodash.keys(dependentAnswers).length === 0) {
    return state;
  }

  logInfo(
    `Applying dependent answers for Question ${sourceQuestionId} (${question.name || sourceQuestionId}).`,
    HpClientDebugFlags.DependentAnswers
  );

  const { groupNumber: sectionGroupNumber } = destructureControlId(sourceQuestionId, { requirePrefix: false });

  const conditionMetAction: HpConditionMetFunction<HpClientDependentAnswer> = ({
    questionId,
    item: dependentAnswer,
    state: currState
  }) => {
    const { questions } = currState;
    let { answers, displayState } = currState;
    let { questionsUI } = displayState;
    let wasChanged = false;
    const { answer, answerExpr } = dependentAnswer;
    const answerValue = answerExpr ? evaluateCalculatedAnswer(question, currState, eventType, answerExpr) : answer;

    if (!isChanged.answers) {
      answers = { ...answers };
      isChanged.answers = true;
      wasChanged = true;
    }

    if (!isChanged.displayStateQuestionsUI) {
      if (!isChanged.displayState) {
        displayState = { ...displayState };
        isChanged.displayState = true;
      }

      questionsUI = { ...questionsUI };
      displayState.questionsUI = questionsUI;
      isChanged.displayStateQuestionsUI = true;
      wasChanged = true;
    }

    const groupQuestionId = createControlId({
      questionId,
      groupNumber: sectionGroupNumber,
      options: { noPrefix: true }
    });

    const answerId = questions[groupQuestionId] ? groupQuestionId : questionId;
    answers[answerId] = applyChanges(answers[answerId], { [answerType]: answerValue });
    const ui = { ...questionsUI[answerId] };
    delete ui.isDirty;
    delete ui.isTouched;
    questionsUI[answerId] = ui;
    if (wasChanged) {
      return applyChanges(currState, { answers, displayState });
    }

    return undefined;
  };

  return applyAnswerDefaults({
    type: 'dependent answer',
    items: dependentAnswers,
    state,
    sourceQuestionId,
    eventType,
    sectionGroupNumber,
    conditionMetAction
  });
}

interface UpdateAnswerDependentActionsProps {
  dependentActions: HpClientDependentAction[];
  eventType: HpEventTypes;
  question: HpClientQuestion;
  questionId: string;
  state: HpClientQuestionSetState;
}

function updateAnswerDependentActions({
  dependentActions,
  eventType,
  question,
  questionId,
  state
}: UpdateAnswerDependentActionsProps): HpClientQuestionSetState {
  logInfo(
    `Applying dependent action${dependentActions.length > 0 ? 's' : ''} for Question ${questionId} (${
      question.name || questionId
    }).`,
    HpClientDebugFlags.DependentActions
  );

  const conditionalModels: SMap<HpClientDependentAction[]> = {};
  conditionalModels[questionId] = dependentActions;
  return runDependentActions({
    type: 'dependent action',
    items: conditionalModels,
    state,
    sourceQuestionId: questionId,
    eventType,
    sectionGroupNumber: undefined,
    conditionMetAction: ({ item: dependentAction, state: condState }) => {
      const { action, props } = dependentAction;
      switch (action) {
        case 'add-control-group': {
          const { groupId } = props as HpAddControlGroupActionProps;
          condState = addControlGroup({ groupId, sourceQuestionId: questionId, state: condState });
          break;
        }

        case 'validate-fields':
        case 'address-select':
          break;

        default: {
          // Button actions should not be executed here, any other action
          // should have an implementation.
          logError(`The "${action}" dependent action currently does nothing.`);
        }
      }

      return condState;
    }
  });
}

export interface HpUpdateAnswerValueProps {
  answers: HpClientAnswerMap;
  clearPartialAnswer?: boolean;
  dataType: HpQuestionDataTypes;
  eventType: HpEventTypes;
  partialAnswer: Partial<HpClientAnswer> | undefined;
  question: HpClientQuestion;
  value: HpAnswerValueTypes;
}

export function updateAnswerValue({
  question,
  answers,
  partialAnswer,
  value,
  dataType,
  eventType,
  clearPartialAnswer
}: HpUpdateAnswerValueProps): HpClientAnswerMap | undefined {
  const { data, questionId } = question;
  const { textAndStyle } = data as HpClientQuestionData;
  const { controlType } = (textAndStyle ? textAndStyle.standard : null) || {};
  const isAutoCompleteList = controlType === 'AutoCompleteList';
  let fieldAnswer = answers[questionId];

  if (!partialAnswer) {
    const customer = correctlyTypeAnswerValue(value as string, dataType, eventType, fieldAnswer, question);

    const { answerValue: originalValue, answerSource } = getAnswerValue(fieldAnswer, question, 'reducer');
    if (answerSource === 'customer' && answersAreSame(dataType, originalValue, value) && !isAutoCompleteList) {
      return undefined;
    }

    partialAnswer = { customer };
  } else {
    if (!hasOwnProperty(partialAnswer, 'partial')) {
      throw new Error("When a 'partialAnswer' is provided, it must contain a 'partial' key.");
    }

    partialAnswer = { customer: value, partial: partialAnswer.partial }; // ensure that only the 'partial' value is used.
  }

  if (isAutoCompleteList) {
    if (clearPartialAnswer || (partialAnswer.customer && eventType === 'click')) {
      partialAnswer.partial = null;
    } else {
      const partialWithList = populateAutoCompleteListIfApplicable(question, partialAnswer);
      if (partialWithList) {
        partialAnswer = partialWithList;
      }
    }
  }

  fieldAnswer = partialAnswer ? applyChanges(fieldAnswer, partialAnswer) : fieldAnswer;
  answers = !isDeveloperEnv ? { ...answers } : sortAnswerMap(answers);
  answers[questionId] = fieldAnswer;
  return answers;
}

export function validateAndMaybeMoveSection(props: HpValidateAndMaybeMoveSectionProps): HpClientQuestionSetState {
  const { buttonAction, desiredSectionId, options = {}, state: currState } = props;
  const { bypassMoveToSectionCheck, raiseNavigationChangedEvent } = options;
  let { isValidationRequired } = options;
  let { displayState, validationResult } = currState;
  const { activeSectionId: currentSectionId, highestSectionStartedPosition, sections } = displayState;
  const currentSectionPosition = sections[currentSectionId].position;

  if (isValidationRequired === undefined) {
    isValidationRequired = buttonActionRequiresValidation(
      sections,
      currentSectionPosition,
      highestSectionStartedPosition,
      buttonAction,
      desiredSectionId
    );
  }

  if (isValidationRequired) {
    validationResult = validateSection({ state: currState });
  } else {
    validationResult = { ...validationResult, errors: {}, checked: {}, count: 0 };
  }

  const { moveToSectionId, validationResult: newValidationResult } = canMoveToDesiredSection({
    buttonAction,
    currentSectionId,
    desiredSectionId,
    displayState,
    validationResult,
    bypassMoveToSectionCheck,
    state: currState
  });

  validationResult = newValidationResult;

  let newState = moveSectionWithoutChecks({ state: currState, moveToSectionId, raiseNavigationChangedEvent });
  displayState = newState.displayState;

  const hasErrors = validationResult.count > 0;
  let scriptActions: HpScriptAction[];
  if (scrollPreference === 'scroll-to-question' && hasErrors) {
    const firstInvalidQuestion = Object.keys(validationResult.errors)[0];
    const scrollToQuestionAction: HpQuestionScriptAction = {
      id: uuid(),
      questionId: firstInvalidQuestion,
      action: scrollPreference
    };
    scriptActions = [scrollToQuestionAction];
  } else {
    scriptActions = [{ id: uuid(), action: 'scroll-to-top' }];
  }

  if (hasErrors) {
    logWarning(validationResult as any, HpClientDebugFlags.None);
  }

  const updatedDisplayState = applyChanges(displayState, { showValidationSummary: hasErrors, scriptActions });
  newState = applyChanges(newState, { validationResult, displayState: updatedDisplayState });

  const validateAndMaybeMoveSectionExtension =
    getCustomExtension<HpValidateAndMaybeMoveSectionExtensionFunction>('validateAndMaybeMoveSection');
  if (validateAndMaybeMoveSectionExtension) {
    newState = validateAndMaybeMoveSectionExtension({ ...props, state: newState });
  }

  return newState;
}

export interface HpDangerouslyMoveSectionProps {
  moveToSectionId: string;
  raiseNavigationChangedEvent?: boolean;
  state: HpClientQuestionSetState;
}

/**
 * This function will move to whichever section is requested without checking if the current section is valid,
 * or if it is beyond the furthest position yet navigated to.
 * @param param
 * @returns The state, with the requested 'moveToSectionId' section loaded.
 */
export function moveSectionWithoutChecks({
  moveToSectionId,
  state: currState,
  raiseNavigationChangedEvent = true
}: HpDangerouslyMoveSectionProps): HpClientQuestionSetState {
  const { productId, questions } = currState;
  let { answers, displayState } = currState;
  const {
    activeSectionId: currentSectionId,
    highestSectionStartedPosition: highestSectionStartedPositionOrig,
    sections
  } = displayState;
  if (currentSectionId === moveToSectionId) {
    return currState;
  }

  const moveToSectionPosition = sections[moveToSectionId].position;
  const highestSectionStartedPosition = Math.max(moveToSectionPosition, highestSectionStartedPositionOrig);
  const visibleQuestionIds = determineVisibleQuestions({
    questions,
    questionsUI: displayState.questionsUI,
    activeSection: displayState.sections[moveToSectionId],
    sections,
    customization: getVisibleQuestionCustomization(productId)
  });

  displayState = { ...displayState };
  answers = { ...answers };

  setActiveSection(displayState, moveToSectionId as string, 'move-to-section', raiseNavigationChangedEvent);

  displayState.visibleQuestionIds = visibleQuestionIds;

  displayState.highestSectionStartedPosition = highestSectionStartedPosition;

  [
    { key: getQuestionIdFor(productId, 'activeSectionId'), customer: moveToSectionId },
    { key: getQuestionIdFor(productId, 'highestSectionStartedPosition'), customer: highestSectionStartedPosition }
  ].forEach(({ key, customer }) => {
    answers[key] = { customer, default: null };
  });

  displayState.sectionsUI = evaluateSectionPrerequisites(displayState, currState, 'click');
  displayState.incrementedSectionId = currentSectionId;

  return applyChanges(currState, { displayState, answers });
}

export interface HpAddControlGroupProps {
  groupId: string; // the group being added
  groupNumber?: number; // the group number to be added (if undefined, it will calculated)
  indexes?: number[]; // the indexes to be added (if undefined, the indexes will be determined via the 'sourceQuestionId')
  processPrereqsAndCalcs?: boolean; // should prerequisites and other calculations be executed?
  sourceQuestionId: string; // the question that requested the group to be added
  state: HpClientQuestionSetState;
}

export function addControlGroup(props: HpAddControlGroupProps): HpClientQuestionSetState {
  const { groupId, groupNumber, processPrereqsAndCalcs: doProcessPrereqsAndCalcs, sourceQuestionId } = props;
  let { indexes, state } = props;

  const questions = { ...state.questions };
  const answers = { ...state.answers };
  const { commonLookups, displayState, productId } = state;
  const {
    activeSectionId,
    allOrderedQuestionIds: allQuestionIdsOriginal,
    questionsUI: questionsUiInitial,
    sections
  } = displayState;

  const allQuestionIds = [...allQuestionIdsOriginal];
  const questionsUI = { ...questionsUiInitial };
  const groupSection = sections[groupId];
  const { isRepeater, questionIds: questionsInGroup, parentId } = groupSection || {};

  if (isRepeater !== true || !questionsInGroup || !questionsInGroup.length) {
    throw new Error(
      `There is no section group "${groupId}" defined by the source question with ID "${sourceQuestionId}" ("${questions[sourceQuestionId].name}").`
    );
  }

  if (parentId && !indexes) {
    const parentSection = ensureDefined(sections[parentId], `sections["${parentId}"]`);

    // if it's not a repeater, we don't care - it's just a normal group inside a top-level section.
    if (parentSection.isRepeater) {
      indexes = getIndexesForRepeatingGroup(sourceQuestionId);
    }
  }

  const groupNo = getNextGroupNumber(questionsInGroup, groupNumber, questions, indexes);
  const position = getStartPositionForGroup(groupNo, questionsInGroup.length);
  const baseOrder: HpQuestionOrderTypes = getBaseOrderForGroup({
    groupId,
    indexes,
    orderedQuestionIds: allQuestionIds,
    questions,
    questionsInGroup,
    sections
  });

  const thisRef = new HpClientPrerequisiteThisReferenceClass({
    answers,
    commonLookups,
    debugFlag: HpClientDebugFlags.DefaultAnswersAndDisabledExpressions,
    displayState,
    groupNumber: undefined,
    indexes: undefined,
    productId,
    questions,
    sections,
    sourceQuestionId,
    sourceQuestionType: getTypeOfQuestion(sourceQuestionId, questions, sections)
  }) as unknown as HpClientPrerequisiteThisReference<HpQuestion>;

  const questionSection = { ...sections[activeSectionId] };
  questionsInGroup.forEach((qid, index) => {
    const questionId = createControlId({
      questionId: qid,
      groupNumber: groupNo,
      indexes,
      options: { noPrefix: true }
    });
    const origQuestion = questions[qid];
    questionsUI[questionId] = { isVisible: true, isEditing: false };
    const order = getQuestionOrderForGroup({ baseOrder, groupNumber: groupNo, indexInGroup: index });
    const question = applyChanges(origQuestion, {
      questionId,
      order,
      position: position + index // Add parent position, or that is order?
    });

    questions[questionId] = question;

    if (!answers[questionId]) {
      if (!createAnswer({ question, questions, answers, sections, thisRef, options: { forRepeater: true } })) {
        answers[questionId] = createBlankAnswer(HpClientDebugFlags.ReducerProcessor);
      }
    }

    if (questionSection.questionIds) {
      // If the section has specific questionIds, add the new group to it.
      if (!questionSection.questionIds.includes(questionId)) {
        questionSection.questionIds = [...questionSection.questionIds, questionId];
      }
    }

    allQuestionIds.push(questionId);
  });

  const allOrderedQuestionIds = allQuestionIds.sort(createQuestionIdSorter(questions));
  const newDisplayState = applyChanges(displayState, {
    questionsUI,
    allOrderedQuestionIds,
    sections: { ...sections, [activeSectionId]: { ...questionSection } }
  });
  state = applyChanges(state, { questions, answers, displayState: newDisplayState });
  if (doProcessPrereqsAndCalcs !== false) {
    state = processPrereqsAndCalcs('click', state);
  }

  return state;
}

export const baseOrderGroupIdToKeyCache = (() => {
  let items: SMap<string> = {};
  return {
    set: (key: string, value: string) => {
      items[key] = value;
    },
    get: (key: string) => items[key],
    clear: () => {
      items = {};
    }
  };
})();

export interface HpGetBaseOrderForGroupProps {
  groupId: string;
  indexes: number[] | undefined;
  orderedQuestionIds: string[];
  questions: HpClientQuestionMap;
  questionsInGroup: string[];
  sections: HpClientSectionMap;
}

// gets the 'order' property of the question with 'order' closest to questions in this group.
// That question must be in the parent section/group (if one exists).
// If this is the second consecutive group, the 'order' returned will be that of the previous group's first question.
export function getBaseOrderForGroup({
  groupId,
  indexes,
  orderedQuestionIds,
  questions,
  questionsInGroup,
  sections
}: HpGetBaseOrderForGroupProps): HpQuestionOrderTypes {
  const key = groupId;
  let foundQuestionId = baseOrderGroupIdToKeyCache.get(key);
  if (foundQuestionId === undefined) {
    const firstId = questionsInGroup[0];
    const section = sections[groupId];
    const parentId = section.parentId || questions[firstId].sectionId;
    let findMode = false;

    foundQuestionId = '';

    for (let i = orderedQuestionIds.length - 1; i >= 0; i--) {
      const id = orderedQuestionIds[i];
      if (findMode) {
        const q = questions[id];
        if (!!q.groupId && q.groupId !== groupId) {
          const qGroupSection = sections[q.groupId];
          if (q.groupId === parentId || (qGroupSection && qGroupSection.parentId === parentId)) {
            const { questionIds } = sections[q.groupId];
            foundQuestionId = (questionIds ? questionIds[0] : null) || id;
            break;
          }
        } else if ((q.groupId || q.sectionId) === parentId) {
          foundQuestionId = id;
          break;
        }
      } else if (id === firstId) {
        findMode = true;
      }
    }

    if (foundQuestionId === '') {
      foundQuestionId = firstId; // this will happen if a group question appears first in a section
    }

    baseOrderGroupIdToKeyCache.set(groupId, foundQuestionId);
  }

  let questionId = foundQuestionId;
  if (indexes !== undefined) {
    questionId = createControlId({ questionId: foundQuestionId, indexes, options: { noPrefix: true } });
  }

  let question = questions[questionId];
  if (question === undefined) {
    if (indexes && indexes.length > 0) {
      const ix = [...indexes];
      const lastIndex = ix.length - 1;
      const lastValue = ix[lastIndex] - 1;
      if (lastValue >= groupStartNumber) {
        ix[lastIndex] = lastValue;
      } else {
        ix.pop();
      }

      if (ix.length > 0) {
        const qid = createControlId({ questionId: foundQuestionId, indexes: ix, options: { noPrefix: true } });
        question = questions[qid];
        if (question !== undefined) {
          return incrementFinalGroupInOrderProperty(question.order);
        }

        // The preceding sibling may be a nested repeater whose instances use multi-part keys
        // (e.g. ~G0001,0000 rather than ~G0001). Try looking up the first inner group instance
        // at the same outer index level before falling back to the base template question.
        const qidWithInner = createControlId({
          questionId: foundQuestionId,
          indexes,
          groupNumber: groupStartNumber,
          options: { noPrefix: true }
        });
        question = questions[qidWithInner];
        if (question !== undefined) {
          return incrementFinalGroupInOrderProperty(question.order);
        }

        // Fall back to the base (un-indexed) template question so we still get a valid order.
        questionId = foundQuestionId;
        question = questions[questionId];
      } else {
        questionId = foundQuestionId;
        question = questions[questionId];
      }
    }

    ensureDefined(question, `questions[${questionId}]`);
  }

  const baseOrder = question.order;
  return baseOrder;
}

function getIndexesForRepeatingGroup(sourceQuestionId: string): number[] | undefined {
  // eslint-disable-next-line prefer-const
  let { indexes, groupNumber } = destructureControlId(sourceQuestionId, {
    requirePrefix: false
  });

  if (groupNumber === undefined) {
    throw new HpError(`The source question ID '${sourceQuestionId}' does not have a group number.`);
  }

  if (indexes === undefined) {
    indexes = [];
  }

  indexes.push(groupNumber);
  return indexes;
}

export interface HpRemoveControlGroupProps {
  clearAnswerIdIfNoGroupsRemain?: string;
  groupId: string;
  groupNumber: number;
  indexes?: number[];
  state: HpClientQuestionSetState;
}

export function removeControlGroup({
  clearAnswerIdIfNoGroupsRemain,
  groupId,
  groupNumber,
  indexes,
  state
}: HpRemoveControlGroupProps): HpClientQuestionSetState {
  const { productId } = state;
  let { answers, displayState, questions, validationResult } = state;
  const { activeSectionId, sections } = displayState;
  let { allOrderedQuestionIds, questionsUI } = displayState;
  const questionIdsInGroup = sections[groupId].questionIds as string[];

  questions = { ...questions };
  answers = { ...answers };
  questionsUI = { ...questionsUI };
  validationResult = {
    ...validationResult,
    errors: { ...validationResult.errors },
    checked: { ...validationResult.checked }
  };
  const idsToRemove = moveQuestionGroupsUpFollowingRemoval(
    questionIdsInGroup,
    groupNumber as number,
    questions,
    answers,
    questionsUI,
    validationResult,
    indexes
  );

  allOrderedQuestionIds = allOrderedQuestionIds.filter((id) => idsToRemove.indexOf(id) < 0);
  displayState = applyChanges(displayState, { allOrderedQuestionIds, questionsUI });

  const visibleQuestionIds = determineVisibleQuestions({
    questions,
    questionsUI,
    activeSection: displayState.sections[activeSectionId],
    sections,
    customization: getVisibleQuestionCustomization(productId)
  });
  displayState = applyChanges(displayState, { visibleQuestionIds });
  state = applyChanges(state, { answers, questions, displayState, validationResult });

  // we must have at least one visible group, so we add a new group if there are none.
  const { questionId: firstQuestionId } = destructureControlId(idsToRemove[0], { requirePrefix: false });
  const firstQuestionFirstGroupId = createControlId({
    questionId: firstQuestionId,
    groupNumber: groupStartNumber,
    indexes,
    options: { noPrefix: true }
  });

  if (!hasOwnProperty(answers, firstQuestionFirstGroupId)) {
    if (!clearAnswerIdIfNoGroupsRemain) {
      state = addControlGroup({
        groupId,
        groupNumber: groupStartNumber,
        processPrereqsAndCalcs: true,
        indexes,
        sourceQuestionId: firstQuestionId,
        state
      });

      return state;
    } else {
      state = applyChanges(state, {
        answers: clearAnswerIfNoGroupsRemain(productId, answers, clearAnswerIdIfNoGroupsRemain)
      });
      return state;
    }
  }

  state = processPrereqsAndCalcs('click', state);
  return state;
}

function clearAnswerIfNoGroupsRemain(
  productId: string,
  answers: HpAnswerMap,
  clearAnswerIdIfNoGroupsRemain: string
): HpAnswerMap {
  let answerId = clearAnswerIdIfNoGroupsRemain;
  let answer = answers[clearAnswerIdIfNoGroupsRemain];
  if (!answer) {
    answerId = getQuestionIdFor(productId, clearAnswerIdIfNoGroupsRemain);
    answer = answers[answerId];
  }

  const newAnswers = applyChanges(answers, {
    [answerId]: applyChanges(answer, { customer: null })
  });

  return newAnswers;
}

export function updateQuestion(
  questionId: string,
  state: HpClientQuestionSetState,
  questionModifier: (q: HpClientQuestion) => HpClientQuestion
): HpClientQuestionSetState {
  return applyInnerChanges(state, ['questions', questionId], questionModifier, {
    copyRootNode: true,
    copyLeafNode: true
  });
}

export function updateDateDropDownQuestionUI(
  detail: HpEventDetail,
  state: HpClientQuestionSetState
): HpClientQuestionSetState {
  const { dateDropDownId } = detail as HpDateDropDownEventDetail;
  const { questionId, key, groupNumber } = destructureControlId(dateDropDownId);
  const blurredDropDownId = createControlId({ questionId, key, options: { noPrefix: true }, groupNumber });
  state = applyInnerChanges(state, ['displayState', 'questionsUI', blurredDropDownId], { isTouched: true });
  return state;
}

export function removeUiProperties(
  state: HpClientQuestionSetState,
  fieldsToModify: string[],
  propsToRemove: (keyof HpClientQuestionUI)[]
): HpClientQuestionSetState {
  return applyInnerChanges(state, ['displayState', 'questionsUI'], (map: HpClientQuestionUiMap) => {
    const res = { ...map };
    fieldsToModify.forEach((f) => {
      const tmp = { ...map[f] };
      propsToRemove.forEach((prop) => {
        delete tmp[prop];
      });

      res[f] = tmp;
    });
    return res;
  });
}

export function populateAutoCompleteListIfApplicable(
  question: HpClientQuestion,
  partialAnswer: Partial<HpClientAnswer> | undefined
): Partial<HpClientAnswer> | undefined {
  const { data, questionId } = question;
  const { textAndStyle, lookup } = data as HpClientQuestionData;
  const { minSearchTermLength } = (textAndStyle ? textAndStyle.standard : null) || {};
  if (!(partialAnswer && lookup)) {
    return undefined;
  }

  const ansVal = partialAnswer ? getAnswerValue(partialAnswer as HpClientAnswer, question, 'prerequisites') : undefined;
  const answerValue: HpAnswerValueTypes = ansVal ? ansVal.answerValue : null;
  const props: HpGetSuggestionsFromMapUsingSearchTermProps<HpClientQuestionLookupItem> = {
    id: questionId,
    items: lookup as SMap<HpClientQuestionLookupItem>,
    searchTerm: (answerValue as string) || '',
    minTermLength: minSearchTermLength
  };
  const partial = recordElapsedTimeOfFunc(
    'getSuggestionsFromMapUsingSearchTerm',
    getSuggestionsFromMapUsingSearchTerm,
    props
  );

  const answer = applyChanges(partialAnswer, { partial });
  return answer;
}

export function correctlyTypeAnswerValue(
  value: string,
  dataType: HpQuestionDataTypes,
  eventType: HpEventTypes,
  fieldAnswer: HpAnswer | undefined,
  question: HpAnswerValueQuestionType
): HpAnswerValueTypes {
  let typedValue: HpAnswerValueTypes = String(value);
  if (dataType === 'array') {
    if (!fieldAnswer) {
      typedValue = [value];
    } else {
      const { answerValue } = getAnswerValue(fieldAnswer, question, 'reducer');
      let answer = (answerValue as string[]) || [];
      // remove value if it exists and add if it does not.
      if (answer.indexOf(value) >= 0) {
        answer = answer.filter((a) => a !== value);
      } else {
        answer = [...answer];
        answer.push(value);
        answer.sort();
      }

      typedValue = answer;
    }
  } else if (dataType === 'boolean') {
    typedValue = isTrue(value);
  } else if (dataType === 'number') {
    if (value === '.') {
      typedValue = value; // allow a number to be entered starting with '.'
    } else if (value.match(/^\d+(\.\d{0,2})?$/) || value.match(/^\.\d{1,2}$/) || !value.length) {
      if (!value.length || value.endsWith('.')) {
        typedValue = value;
      } else {
        typedValue = parseFloat(value);
      }
    }
  } else if (eventType === 'blur') {
    typedValue = lodash.trim(typedValue);
  }

  return typedValue;
}

// Modifies questions, answers, questionsUI and validationResult
// Returns the IDs to really remove (given we've bumped everything up by one).
function moveQuestionGroupsUpFollowingRemoval(
  questionIdsInGroup: string[],
  groupNumberRemoved: number,
  questions: HpClientQuestionMap,
  answers: HpClientAnswerMap,
  questionsUI: HpClientQuestionUiMap,
  validationResult: HpClientValidationResult,
  indexes?: number[]
): string[] {
  const firstQId = questionIdsInGroup[0];
  const options: HpCreateControlIdOptions = { noPrefix: true };
  let highestOrderInGroup: HpQuestionOrderTypes = -1;
  let idsToRemove: string[] = questionIdsInGroup.map((questionId) => {
    const { order } = questions[questionId] || {};
    if (order > highestOrderInGroup) {
      highestOrderInGroup = order;
    }

    return createControlId({ questionId, groupNumber: groupNumberRemoved, indexes, options });
  });
  let copyToIds = idsToRemove;

  for (let fromGrpNo = groupNumberRemoved + 1; fromGrpNo < maxGroupsAllowed; fromGrpNo++) {
    const toGrpNo = fromGrpNo - 1;
    const questionFromGroupId = createControlId({ questionId: firstQId, groupNumber: fromGrpNo, indexes, options });
    if (hasOwnProperty(answers, questionFromGroupId)) {
      const copyFromIds = questionIdsInGroup.map((id) =>
        createControlId({ questionId: id, groupNumber: fromGrpNo, indexes, options })
      );

      for (let j = 0; j < copyFromIds.length; j++) {
        const order = getQuestionOrderForGroup({
          baseOrder: highestOrderInGroup,
          groupNumber: toGrpNo,
          indexInGroup: j
        });
        const copyToId = copyToIds[j];
        const copyFromId = copyFromIds[j];
        answers[copyToId] = answers[copyFromId];
        questions[copyToId] = applyChanges(questions[copyFromId], { questionId: copyToId, order });
        questionsUI[copyToId] = questionsUI[copyFromId];
      }

      copyToIds = copyFromIds;
    } else {
      break;
    }
  }

  idsToRemove = copyToIds;
  idsToRemove.forEach((id) => {
    delete answers[id];
    delete questions[id];
    delete questionsUI[id];
    if (validationResult.errors[id]) {
      validationResult.count--;
      delete validationResult.errors[id];
    }

    delete validationResult.checked[id];
  });

  return idsToRemove;
}

export interface HpGetQuestionOrderProps {
  baseOrder: HpQuestionOrderTypes;
  groupNumber: number;
  indexInGroup: number; // the index of the question in the group
}

export function incrementFinalGroupInOrderProperty(order: HpQuestionOrderTypes): HpQuestionOrderTypes {
  if (questionOrderPropertyIsString) {
    return incrementFinalGroupInOrderStringProperty(String(order));
  }

  return incrementFinalGroupInOrderNumericProperty(1.0 * (order as number));
}

function incrementFinalGroupInOrderStringProperty(order: string): string {
  const parts = order.split('.');
  if (parts.length === 1) {
    throw new HpError(`Unable to increment 'order' '${order}' because no group value is defined`);
  }

  const lastIndex = parts.length - 1;
  const end = parts[lastIndex];
  const groupNumber = parseInt(end.slice(0, groupIndexWidth)) + 1;
  const indexInGroup = parseInt(end.slice(groupIndexWidth));
  const groupPart = `${groupNumber}`.padStart(groupIndexWidth, '0') + `${indexInGroup}`.padStart(groupIndexWidth, '0');
  parts[lastIndex] = groupPart;
  return parts.join('.');
}

function incrementFinalGroupInOrderNumericProperty(order: number): number {
  if (isNaN(order)) {
    throw new HpError('Unable to increment "order" because the value provided is NaN');
  }

  if (groupStartNumber === 0) {
    throw new HpError(`When "questionOrderPropertyIsString" is false, "groupStartNumber" cannot be 0`);
  }

  const precision = groupIndexWidth * 2 * maxGroupNesting;
  if (precision > 12) {
    throw new HpError(
      // Considering that 100.01 - 100.0 = 0.010000000000005116, a safe precision has been set at 12
      `Due to restictions on floating point precision, your selection of "maxGroupNesting" (${maxGroupNesting}) and "maxGroupsAllowed" (${maxGroupsAllowed}) is to large`
    );
  }

  let orderString = (order as number).toFixed(precision);
  const fpIndex = orderString.indexOf('.');
  const giw = groupIndexWidth * 2;
  const none = '0'.repeat(giw);
  const fraction = orderString.slice(fpIndex + 1);

  let parts: string[] = [];
  for (let i = 0; i < fraction.length; i += giw) {
    const part = fraction.slice(i, i + giw);
    parts.push(part);
  }

  while (parts[parts.length - 1] === none) {
    parts.pop();
  }

  if (parts.length > 0) {
    parts.unshift(''); // force a dot in the join below.
  }

  orderString = orderString.slice(0, fpIndex) + parts.join('.');
  orderString = incrementFinalGroupInOrderStringProperty(orderString);
  parts = orderString.split('.');
  orderString = parts[0] + '.' + parts.slice(1).join('');
  return parseFloat(orderString);
}

export function getQuestionOrderForGroup({
  baseOrder: baseOrderStringOrNumber,
  groupNumber,
  indexInGroup
}: HpGetQuestionOrderProps): HpQuestionOrderTypes {
  if (questionOrderPropertyIsString) {
    // with this implementation we can ignore 'maxGroupNesting' because we will never lose precision (as we do with number type)
    const baseOrder = String(baseOrderStringOrNumber);
    let res = baseOrder;
    const groupPart =
      `${groupNumber}`.padStart(groupIndexWidth, '0') + `${indexInGroup}`.padStart(groupIndexWidth, '0');
    res += '.' + groupPart;
    return res;
  }

  const baseOrder = baseOrderStringOrNumber as number;
  const groupAndIndex = 2; // group takes one space, and index takes another;
  const block = Math.pow(10, groupIndexWidth * groupAndIndex);
  const maxAllowed = Math.pow(10, groupIndexWidth * groupAndIndex * maxGroupNesting);
  const maxPermitted = Math.pow(10, 14); // hard-maximum, due to rounding issues
  if (maxAllowed > maxPermitted) {
    throw new HpError(`The maximum allowed value ${maxAllowed} exceeds 10^14 and cannot be processed.`);
  }

  const fraction = getFractionalPart(baseOrder, maxAllowed);
  let nesting = 0;
  let tot = fraction;
  while (tot > 0) {
    nesting++;
    tot = getFractionalPart(tot * block, maxAllowed);
  }

  if (nesting >= maxGroupNesting) {
    throw new HpError(`The maximum allowed nesting level, ${maxGroupNesting}, has been exceeded.`);
  }

  const divider = Math.pow(10, groupIndexWidth * groupAndIndex * nesting);

  // calculate group and index as fixed width numeric fraction
  let toAdd = groupNumber / multiplePerIndex / divider;
  if (indexInGroup > 0) {
    toAdd += indexInGroup / (multiplePerIndex * multiplePerIndex) / divider;
  }

  let res = baseOrder + toAdd;
  res = Math.round(res * maxAllowed) / maxAllowed;
  return res;
}

function getFractionalPart(num: number, rounding: number): number {
  return Math.round((num - Math.floor(num)) * rounding) / rounding;
}

/**
 * Gets the start position for the group.
 * @param groupNumber The group number (starting from 1).
 * @param questionsInGroupCount The number of questions in the group.
 * @returns The position of the first question in the group.
 */
export function getStartPositionForGroup(groupNumber: number, questionsInGroupCount: number): number {
  // The 10000 is arbitrary - as long as it is greater than the number of questions.
  // It also doesn't matter if repeated questions in different groups end up with an identical position.
  return 10000 + (groupNumber - groupStartNumber) * questionsInGroupCount;
}

function buttonActionRequiresValidation(
  sections: HpClientSectionMap,
  currentSectionPosition: number,
  highestSectionStartedPosition: number,
  buttonAction: HpMoveToSectionTypes,
  desiredSectionId: string | undefined
): boolean {
  const isAtFarthestStartedPosition = currentSectionPosition === highestSectionStartedPosition;

  let isValidationRequired = true;
  if (desiredSectionId) {
    if (buttonAction !== moveToSectionType) {
      throw new Error(
        `When 'desiredSectionId' is provided ('${desiredSectionId}'), 'buttonAction' must be '${moveToSectionType}' (not '${buttonAction}').`
      );
    }

    const desiredSection = ensureDefined(sections[desiredSectionId], `section with ID '${desiredSectionId}'`);
    const desiredSectionPosition = desiredSection.position;

    if (desiredSectionPosition < currentSectionPosition) {
      isValidationRequired = !isAtFarthestStartedPosition;

      if (!validateOnBackNavigation) {
        isValidationRequired = false;
      }
    }
  } else if (buttonAction === 'previous-section') {
    isValidationRequired = !isAtFarthestStartedPosition;

    if (!validateOnBackNavigation) {
      isValidationRequired = false;
    }
  }

  return isValidationRequired;
}

export function getNextGroupNumber(
  questionsInGroup: string[],
  groupNumber: number | undefined,
  questions: SMap<any>, // in practice, a HpQuestionMap is prefered
  indexes: number[] | undefined
): number {
  if (groupNumber === undefined) {
    const questionIdFirst = questionsInGroup[0];
    let grpNum = groupStartNumber - 1;
    let questionId: string;
    do {
      questionId = createControlId({
        questionId: questionIdFirst,
        groupNumber: ++grpNum,
        indexes: indexes,
        options: { noPrefix: true }
      });
    } while (questions[questionId]);

    groupNumber = grpNum;
  }

  return groupNumber;
}

export interface HpEvaluateConditionProps {
  condition: HpClientConditionFunction;
  debugFlag: HpClientDebugFlags;
  eventType: HpEventTypes;
  groupNumber?: number;
  indexes?: number[];
  questionId: string;
  questionSet: HpClientQuestionSetState;
  sourceQuestionId: string;
}

export function evaluateCondition({
  condition,
  debugFlag,
  eventType,
  groupNumber,
  indexes,
  questionId,
  questionSet,
  sourceQuestionId
}: HpEvaluateConditionProps): boolean {
  const { answers, commonLookups, displayState, productId, questions } = questionSet;
  const { sections } = displayState;

  const thisArg = new HpClientPrerequisiteThisReferenceClass({
    answers,
    commonLookups,
    debugFlag,
    displayState,
    groupNumber,
    indexes,
    productId,
    questions,
    sections,
    sourceQuestionId,
    sourceQuestionType: getTypeOfQuestion(sourceQuestionId, questions, sections)
  });

  const conditionProps: HpClientConditionFunctionProps = {
    eventType,
    groupNumber,
    indexes,
    lookupKey: '',
    questionId,
    questionType: getTypeOfQuestion(questionId, questions, sections),
    sourceQuestionId
  };

  return condition.apply(thisArg, [conditionProps]);
}

interface HpAddCalculationsToIdentierConfigValues {
  groupIndexWidth: number;
  groupStartNumber: number;
  maxGroupNesting: number;
  maxGroupsAllowed: number;
  multiplePerIndex: number;
  questionOrderPropertyIsString: boolean;
  useDescriptiveQuestionIds: SMap<boolean>;
}

function addCalculationsToIdentierConfig({
  groupIndexWidth: giw,
  groupStartNumber: gsn,
  maxGroupNesting: mgn,
  maxGroupsAllowed: mga,
  questionOrderPropertyIsString: qopis,
  useDescriptiveQuestionIds: udqids
}: HpIdentifierAndFunctionValues): HpAddCalculationsToIdentierConfigValues {
  const res: HpAddCalculationsToIdentierConfigValues = {
    groupStartNumber: gsn,
    multiplePerIndex: Math.pow(10, giw),
    groupIndexWidth: giw,
    maxGroupNesting: mgn,
    maxGroupsAllowed: mga,
    questionOrderPropertyIsString: qopis,
    useDescriptiveQuestionIds: udqids
  };
  return res;
}
